import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import RichTextEditor from "../policy/RichTextEditor";
import { useParams, useHistory } from "react-router-dom";
import { Container, Row, Col, Card, Button, Form, Alert } from "react-bootstrap";
import { Redirect } from "react-router-dom";
import axios from "axios";
import styles from "./UserManual.module.scss";

export function UserManualForm() {
  const { id } = useParams();
  const history = useHistory();
  const user = useSelector((state) => state.authentication?.user);
  const [tabName, setTabName] = useState("");
  const [content, setContent] = useState("");
  const [sortIndex, setSortIndex] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      loadManualDetails();
    }
  }, [id]);

  const loadManualDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = JSON.parse(localStorage.getItem('auth') || '{}');
      const token = auth.access_token;
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const response = await axios.get(
        `${baseUrl}/api/user_manual/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success && response.data.data) {
        const manual = response.data.data;
        setTabName(manual.tab_name || "");
        setContent(manual.content || "");
        setSortIndex(manual.sort_index || 0);
        setIsActive(manual.is_active ?? true);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error loading user manual section");
      console.error("Error loading manual:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowSuccess(false);

    try {
      const auth = JSON.parse(localStorage.getItem('auth') || '{}');
      const token = auth.access_token;
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const payload = {
        tab_name: tabName,
        content: content,
        sort_index: parseInt(sortIndex),
        is_active: isActive
      };

      let response;
      if (id) {
        // Update existing manual
        response = await axios.post(
          `${baseUrl}/api/user_manual/${id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // Create new manual
        response = await axios.post(
          `${baseUrl}/api/user_manual`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      if (response.data.success) {
        setShowSuccess(true);
        setTimeout(() => {
          history.push('/user-manual/list');
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error saving user manual section");
      console.error("Error saving manual:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push('/user-manual/list');
  };

  // Admin or QA access guard
  if (!(user?.is_admin || user?.is_admin_or_qa)) {
    return <Redirect to="/" />;
  }

  if (loading && id) {
    return (
      <Container fluid className={styles['manual-container']}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
      <Container fluid className={styles['manual-container']}>
      <Row>
        <Col>
          <Card className={styles['manual-card']}>
            <Card.Header className={styles['card-header']}>
              <h3>{id ? 'Edit' : 'Create'} User Manual Section</h3>
            </Card.Header>
            <Card.Body className={styles['card-body']}>
              {showSuccess && (
                <Alert variant="success" dismissible onClose={() => setShowSuccess(false)} className="mb-3">
                  User manual section saved successfully!
                </Alert>
              )}
              
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className={styles['form-label']}>Tab Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Tab name will be displayed in the navigation tabs"
                    value={tabName}
                    onChange={(e) => setTabName(e.target.value)}
                    required
                    disabled={loading}
                    className={styles['form-input']}
                  />
                  {/* <Form.Text className="text-muted">
                    This will be displayed in the navigation tabs
                  </Form.Text> */}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className={styles['form-label']}>Sort Index <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Lower values appear first (0 = first, 1 = second, etc.)"
                    value={sortIndex}
                    onChange={(e) => setSortIndex(e.target.value)}
                    required
                    min="0"
                    disabled={loading}
                    className={styles['form-input']}
                  />
                  {/* <Form.Text className="text-muted">
                    Lower values appear first (0 = first, 1 = second, etc.)
                  </Form.Text> */}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className={styles['form-label']}>Content <span className="text-danger">*</span></Form.Label>
                  <RichTextEditor 
                    value={content}
                    onChange={setContent}
                    placeholder="Enter the content for this section (HTML supported). Heading 2 will have bottom border."
                  />
                  {/* <Form.Text className="text-muted">
                    Use the editor to format your content with headings, lists, tables, etc.
                  </Form.Text> */}
                </Form.Group>

                <Form.Group className="mb-3 text-black">
                  <Form.Check
                    type="checkbox"
                    label="Active (visible to users)"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={loading}
                  />
                </Form.Group>

                <div className={styles['button-group']}>
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={loading || !tabName || !content}
                  >
                    {loading ? 'Saving...' : (id ? 'Update' : 'Create')} Section
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default UserManualForm;

